// Tabs — a controlled, accessible tab strip (ARIA tablist/tab + roving arrow-key
// nav). Underline style on the glass theme. Pages own the selected value so a
// tab can be URL-synced (e.g. /events?tab=alarms). Optional per-tab `count`
// renders a trailing badge for "Active (12)" affordances.
import { useRef, type KeyboardEvent } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface TabDef<V extends string = string> {
  value: V;
  label: string;
  icon?: LucideIcon;
  count?: number;
}

export function Tabs<V extends string = string>({
  tabs, value, onChange, className = '', 'aria-label': ariaLabel,
}: {
  tabs: TabDef<V>[];
  value: V;
  onChange: (value: V) => void;
  className?: string;
  'aria-label'?: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  const onKeyDown = (e: KeyboardEvent, idx: number) => {
    const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!dir) return;
    e.preventDefault();
    const next = (idx + dir + tabs.length) % tabs.length;
    refs.current[next]?.focus();
    onChange(tabs[next].value);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`flex items-center gap-1 border-b border-white/10 ${className}`}
    >
      {tabs.map((tab, i) => {
        const active = tab.value === value;
        const Icon = tab.icon;
        return (
          <button
            key={tab.value}
            ref={(el) => { refs.current[i] = el; }}
            role="tab"
            type="button"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(tab.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={`relative -mb-px flex items-center gap-2 px-3 py-2 text-body font-medium transition-colors ${
              active ? 'text-fg' : 'text-muted hover:text-fg'
            }`}
          >
            {Icon && <Icon size={15} className={active ? 'text-primary' : ''} />}
            {tab.label}
            {tab.count !== undefined && (
              <span className={`rounded-full px-1.5 text-caption tabular-nums ${active ? 'bg-primary/15 text-primary' : 'bg-white/5 text-muted'}`}>
                {tab.count}
              </span>
            )}
            {active && (
              <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-gradient-to-r from-primary to-accent" />
            )}
          </button>
        );
      })}
    </div>
  );
}
